import React from "react";
import { Send, X, Check, Clock, CircleDashed } from "lucide-react";
import { MCC_ACCOUNTS } from "../../config/constants";
import { useCase } from "../../context/CaseContext";

const HandoverModal: React.FC = () => {
  const {
    showHandoverModal: show,
    setShowHandoverModal: setShow,
    clientName,
    handoverData,
    setHandoverData,
    handleHandoverSubmit: onSubmit,
    isSendingHandover: isSending,
  } = useCase();

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-200 dark:border-slate-800 animate-in zoom-in-95">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-white dark:bg-slate-900">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Send className="text-blue-600" size={20} /> Handover Project
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Create Notion project for{" "}
              <span className="font-bold text-slate-700 dark:text-slate-300">
                {clientName || "Client"}
              </span>
            </p>
          </div>
          <button
            onClick={() => setShow(false)}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Priority Toggle - Styled like a card */}
          <div
            onClick={() =>
              setHandoverData({
                ...handoverData,
                priority: !handoverData.priority,
              })
            }
            className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${handoverData.priority ? "border-blue-600 bg-blue-50 dark:bg-blue-900/20" : "border-gray-100 dark:border-slate-800 hover:border-blue-200 dark:hover:border-slate-700"}`}
          >
            <div
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${handoverData.priority ? "border-blue-600 bg-blue-600" : "border-gray-300 dark:border-slate-600"}`}
            >
              {handoverData.priority && (
                <Check size={14} className="text-white" />
              )}
            </div>
            <div>
              <h4
                className={`text-sm font-bold ${handoverData.priority ? "text-blue-700 dark:text-blue-400" : "text-slate-700 dark:text-slate-300"}`}
              >
                High Priority Project
              </h4>
              <p className="text-xs text-slate-500">
                Mark this project for immediate attention
              </p>
            </div>
          </div>

          {/* Grid inputs */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase text-gray-500 dark:text-slate-400">
                Expected Hours
              </label>
              <div className="relative">
                <Clock
                  className="absolute left-3 top-2.5 text-gray-400"
                  size={16}
                />
                <input
                  type="number"
                  className="w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                  placeholder="0.0"
                  value={handoverData.hours}
                  onChange={(e) =>
                    setHandoverData({
                      ...handoverData,
                      hours: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase text-gray-500 dark:text-slate-400">
                Deadline
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                value={handoverData.endDate}
                onChange={(e) =>
                  setHandoverData({
                    ...handoverData,
                    endDate: e.target.value,
                  })
                }
              />
            </div>
          </div>

          {/* Assignee & MCC */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase text-gray-500 dark:text-slate-400">
                MCC Account
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                value={handoverData.mcc}
                onChange={(e) =>
                  setHandoverData({ ...handoverData, mcc: e.target.value })
                }
              >
                {MCC_ACCOUNTS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase text-gray-500 dark:text-slate-400">
                Assignee
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                value={handoverData.assignee}
                onChange={(e) =>
                  setHandoverData({
                    ...handoverData,
                    assignee: e.target.value,
                  })
                }
              >
                <option value="Florian">Florian</option>
                <option value="Maurice">Maurice</option>
                <option value="Dobromir">Dobromir</option>
                <option value="External">External</option>
              </select>
            </div>
          </div>

          {/* Custom Assignee conditional */}
          {handoverData.assignee === "External" && (
            <div className="space-y-1.5 animate-in fade-in">
              <label className="text-[11px] font-bold uppercase text-gray-500 dark:text-slate-400">
                External Name
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-200 dark:border-slate-700 rounded-lg text-sm dark:bg-slate-800 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
                placeholder="Enter name..."
                value={handoverData.assigneeCustom}
                onChange={(e) =>
                  setHandoverData({
                    ...handoverData,
                    assigneeCustom: e.target.value,
                  })
                }
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-950/50 flex justify-end gap-3">
          <button
            onClick={() => setShow(false)}
            className="px-4 py-2 text-slate-600 dark:text-slate-400 font-bold hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={isSending}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold shadow-lg shadow-blue-200 dark:shadow-none flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed transition-all"
          >
            {isSending ? (
              <>
                <CircleDashed className="w-4 h-4 animate-spin" /> Processing...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" /> Confirm & Create
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HandoverModal;
